// --- START OF FILE src/theme.js ---

import { createTheme, alpha } from '@mui/material/styles';

export const theme = createTheme({
    palette: {
        primary: { 
            main: '#2563EB', // Blu brillante
            light: '#60A5FA',
            dark: '#1D4ED8',
            contrastText: '#FFFFFF'
        },
        secondary: { 
            main: '#F97316', // Arancione vivace
            light: '#FDBA74',
            dark: '#EA580C',
            contrastText: '#FFFFFF'
        },
        background: { 
            default: '#F8FAFC', 
            paper: '#FFFFFF' 
        },
        text: { 
            primary: '#0F172A', 
            secondary: '#475569', 
            disabled: '#94A3B8' 
        },
        info: { 
            light: '#7DD3FC', 
            main: '#0EA5E9', 
            dark: '#0284C7' 
        },
        success: { 
            light: '#86EFAC', 
            main: '#22C55E', 
            dark: '#16A34A' 
        },
        warning: { 
            light: '#FDE68A', 
            main: '#EAB308', 
            dark: '#CA8A04' 
        },
        error: { 
            light: '#FCA5A5', 
            main: '#EF4444', 
            dark: '#DC2626' 
        },
        grey: { 
            50: '#F8FAFC', 
            100: '#F1F5F9', 
            200: '#E2E8F0', 
            300: '#CBD5E1', 
            400: '#94A3B8',
            500: '#64748B', 
            600: '#475569',
            700: '#334155',
            800: '#1E293B',
            900: '#0F172A'
        },
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        fontWeightBold: 700,
        h1: { 
            fontWeight: 700,
            fontSize: '2.5rem',
            lineHeight: 1.2,
            letterSpacing: '-0.01562em'
        },
        h2: { 
            fontWeight: 700,
            fontSize: '2rem',
            lineHeight: 1.2,
            letterSpacing: '-0.00833em'
        },
        h3: { 
            fontWeight: 600,
            fontSize: '1.75rem',
            lineHeight: 1.2,
            letterSpacing: '0em'
        },
        h4: { 
            fontWeight: 600,
            fontSize: '1.5rem',
            lineHeight: 1.2,
            letterSpacing: '0.00735em'
        },
        h5: { 
            fontWeight: 600,
            fontSize: '1.25rem',
            lineHeight: 1.2,
            letterSpacing: '0em'
        },
        h6: { 
            fontWeight: 600,
            fontSize: '1rem',
            lineHeight: 1.2,
            letterSpacing: '0.0075em'
        },
        subtitle1: {
            fontWeight: 500,
            fontSize: '1rem',
            lineHeight: 1.5,
            letterSpacing: '0.00938em'
        },
        subtitle2: {
            fontWeight: 500,
            fontSize: '0.875rem',
            lineHeight: 1.57,
            letterSpacing: '0.00714em'
        },
        body1: {
            fontSize: '1rem',
            lineHeight: 1.5,
            letterSpacing: '0.00938em'
        },
        body2: {
            fontSize: '0.875rem',
            lineHeight: 1.43,
            letterSpacing: '0.01071em'
        },
        button: { 
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
            letterSpacing: '0.02857em'
        },
        caption: {
            fontSize: '0.75rem',
            lineHeight: 1.66,
            letterSpacing: '0.03333em'
        },
        overline: {
            fontSize: '0.75rem',
            lineHeight: 2.66,
            letterSpacing: '0.08333em',
            textTransform: 'uppercase'
        }
    },
    shape: { 
        borderRadius: 12 
    },
    shadows: [
        'none',
        '0px 2px 1px -1px rgba(0,0,0,0.05),0px 1px 1px 0px rgba(0,0,0,0.04),0px 1px 3px 0px rgba(0,0,0,0.03)',
        '0px 3px 3px -2px rgba(0,0,0,0.05),0px 2px 6px 0px rgba(0,0,0,0.04),0px 1px 8px 0px rgba(0,0,0,0.03)',
        '0px 3px 4px -2px rgba(0,0,0,0.06),0px 3px 8px -1px rgba(0,0,0,0.05),0px 1px 10px 0px rgba(0,0,0,0.04)',
        '0px 2px 4px -1px rgba(0,0,0,0.06),0px 4px 10px -1px rgba(0,0,0,0.05),0px 1px 12px 0px rgba(0,0,0,0.04)',
        '0px 3px 5px -1px rgba(0,0,0,0.07),0px 5px 12px -2px rgba(0,0,0,0.06),0px 1px 15px 0px rgba(0,0,0,0.05)',
        '0px 3px 5px -1px rgba(0,0,0,0.07),0px 6px 16px -2px rgba(0,0,0,0.06),0px 1px 18px 0px rgba(0,0,0,0.05)',
        '0px 4px 5px -2px rgba(0,0,0,0.07),0px 7px 18px -2px rgba(0,0,0,0.06),0px 2px 20px 0px rgba(0,0,0,0.05)',
        '0px 5px 6px -3px rgba(0,0,0,0.07),0px 9px 20px -2px rgba(0,0,0,0.06),0px 3px 22px 0px rgba(0,0,0,0.05)',
        '0px 5px 6px -3px rgba(0,0,0,0.07),0px 10px 22px -2px rgba(0,0,0,0.06),0px 4px 24px 0px rgba(0,0,0,0.05)',
        '0px 6px 7px -4px rgba(0,0,0,0.07),0px 11px 24px -3px rgba(0,0,0,0.06),0px 5px 26px 0px rgba(0,0,0,0.05)',
        '0px 6px 8px -4px rgba(0,0,0,0.07),0px 12px 26px -3px rgba(0,0,0,0.06),0px 6px 28px 0px rgba(0,0,0,0.05)',
        '0px 7px 9px -4px rgba(0,0,0,0.07),0px 14px 28px -3px rgba(0,0,0,0.06),0px 7px 30px 0px rgba(0,0,0,0.05)',
        '0px 7px 10px -4px rgba(0,0,0,0.07),0px 16px 30px -3px rgba(0,0,0,0.06),0px 8px 32px 0px rgba(0,0,0,0.05)',
        '0px 8px 11px -5px rgba(0,0,0,0.07),0px 18px 32px -3px rgba(0,0,0,0.06),0px 9px 34px 0px rgba(0,0,0,0.05)',
        '0px 9px 12px -6px rgba(0,0,0,0.07),0px 20px 34px -3px rgba(0,0,0,0.06),0px 10px 36px 0px rgba(0,0,0,0.05)',
        '0px 10px 13px -6px rgba(0,0,0,0.07),0px 22px 36px -3px rgba(0,0,0,0.06),0px 11px 38px 0px rgba(0,0,0,0.05)',
        '0px 10px 14px -6px rgba(0,0,0,0.07),0px 24px 38px -3px rgba(0,0,0,0.06),0px 12px 40px 0px rgba(0,0,0,0.05)',
        '0px 11px 15px -7px rgba(0,0,0,0.07),0px 26px 40px -3px rgba(0,0,0,0.06),0px 13px 42px 0px rgba(0,0,0,0.05)',
        '0px 12px 16px -7px rgba(0,0,0,0.07),0px 28px 42px -3px rgba(0,0,0,0.06),0px 14px 44px 0px rgba(0,0,0,0.05)',
        '0px 12px 17px -7px rgba(0,0,0,0.07),0px 30px 44px -3px rgba(0,0,0,0.06),0px 15px 46px 0px rgba(0,0,0,0.05)',
        '0px 13px 18px -8px rgba(0,0,0,0.07),0px 32px 46px -3px rgba(0,0,0,0.06),0px 16px 48px 0px rgba(0,0,0,0.05)',
        '0px 14px 19px -8px rgba(0,0,0,0.07),0px 34px 48px -3px rgba(0,0,0,0.06),0px 17px 50px 0px rgba(0,0,0,0.05)',
        '0px 14px 20px -8px rgba(0,0,0,0.07),0px 36px 50px -3px rgba(0,0,0,0.06),0px 18px 52px 0px rgba(0,0,0,0.05)',
        '0px 15px 21px -9px rgba(0,0,0,0.07),0px 38px 52px -3px rgba(0,0,0,0.06),0px 19px 54px 0px rgba(0,0,0,0.05)',
    ],
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                '::selection': {
                    backgroundColor: alpha('#2563EB', 0.2),
                },
                body: {
                    scrollbarWidth: 'thin',
                    '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: '#F1F5F9',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: '#CBD5E1',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: '#94A3B8',
                    }
                }
            }
        },
        MuiPaper: { 
            defaultProps: { 
                elevation: 0 
            }, 
            styleOverrides: { 
                root: { 
                    backgroundImage: 'none',
                    transition: 'box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                }
            }
        },
        MuiCard: { 
            styleOverrides: { 
                root: { 
                    boxShadow: '0 2px 16px rgba(0,0,0,0.08)', 
                    border: 'none', 
                    borderRadius: 16,
                    overflow: 'hidden',
                    transition: 'box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                    '&:hover': {
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        transform: 'translateY(-2px)'
                    }
                }
            }
        },
        MuiButton: { 
            defaultProps: {
                disableElevation: true
            },
            styleOverrides: { 
                root: { 
                    borderRadius: 12,
                    padding: '8px 22px',
                    fontWeight: 500,
                    transition: 'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                    '&:hover': {
                        transform: 'translateY(-1px)'
                    },
                    '&:active': {
                        transform: 'translateY(1px)'
                    }
                },
                contained: {
                    boxShadow: '0 3px 5px -1px rgba(0,0,0,0.06), 0px 6px 10px 0px rgba(0,0,0,0.042), 0px 1px 18px 0px rgba(0,0,0,0.036)',
                    '&:hover': {
                        boxShadow: '0 6px 10px -3px rgba(0,0,0,0.1), 0px 10px 14px 1px rgba(0,0,0,0.07), 0px 4px 18px 3px rgba(0,0,0,0.06)'
                    }
                },
                outlined: {
                    borderWidth: '1.5px',
                    '&:hover': {
                        borderWidth: '1.5px'
                    }
                },
                text: {
                    padding: '8px 12px'
                },
                containedPrimary: {
                    '&:hover': {
                        backgroundColor: '#1D4ED8'
                    }
                },
                containedSecondary: {
                    '&:hover': {
                        backgroundColor: '#EA580C'
                    }
                }
            }
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    transition: 'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                    '&:hover': {
                        transform: 'translateY(-1px)'
                    },
                    '&:active': {
                        transform: 'translateY(1px)'
                    }
                }
            }
        },
        MuiTextField: { 
            defaultProps: { 
                variant: 'outlined', 
                size: 'medium' 
            }, 
            styleOverrides: { 
                root: { 
                    '& .MuiOutlinedInput-root': { 
                        borderRadius: 12,
                        transition: 'box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                        '&.Mui-focused': {
                            boxShadow: '0 0 0 4px rgba(37, 99, 235, 0.1)'
                        }
                    }
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#94A3B8'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 2
                    }
                },
                notchedOutline: {
                    transition: 'border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms'
                }
            }
        },
        MuiFilledInput: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    '&:before': {
                        borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
                    },
                    '&:hover:before': {
                        borderBottom: '1px solid rgba(0, 0, 0, 0.32)'
                    }
                }
            }
        },
        MuiSelect: { 
            defaultProps: { 
                variant: 'outlined', 
                size: 'medium' 
            }, 
            styleOverrides: { 
                root: { 
                    borderRadius: 12
                }
            }
        },
        MuiTableCell: { 
            styleOverrides: {
                head: ({ theme }) => ({ 
                    fontWeight: 600, 
                    color: theme.palette.text.secondary, 
                    backgroundColor: theme.palette.grey[50], 
                    borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    padding: '16px',
                    fontSize: '0.875rem'
                }),
                body: ({ theme }) => ({ 
                    borderBottom: `1px solid ${theme.palette.grey[100]}`,
                    padding: '16px'
                }),
            }
        },
        MuiTableContainer: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                    overflow: 'hidden'
                }
            }
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:last-child td': {
                        borderBottom: 0
                    },
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.01)'
                    }
                }
            }
        },
        MuiChip: { 
            styleOverrides: { 
                root: { 
                    borderRadius: 8, 
                    fontWeight: 500,
                    transition: 'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                    '&:hover': {
                        boxShadow: '0 2px 4px rgba(0,0,0,0.06)'
                    }
                },
                label: {
                    padding: '0 12px'
                },
                icon: {
                    color: 'inherit'
                },
                deleteIcon: {
                    color: 'inherit',
                    '&:hover': {
                        color: 'inherit',
                        opacity: 0.7
                    }
                }
            }
        },
        MuiDialog: { 
            styleOverrides: { 
                paper: { 
                    borderRadius: 16, 
                    boxShadow: '0 24px 48px rgba(0,0,0,0.16)',
                    overflow: 'hidden'
                }
            }
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    padding: '24px 24px 16px'
                }
            }
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    padding: '16px 24px 24px'
                }
            }
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: '12px 24px 24px'
                }
            }
        },
        MuiAppBar: { 
            defaultProps: { 
                elevation: 0, 
                color: 'inherit'
            }, 
            styleOverrides: { 
                root: ({ theme }) => ({ 
                    backgroundColor: alpha(theme.palette.background.paper, 0.9),
                    backdropFilter: 'blur(10px)',
                    borderBottom: `1px solid ${theme.palette.grey[100]}` 
                }) 
            }
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    borderRight: '1px solid rgba(0, 0, 0, 0.08)'
                }
            }
        },
        MuiMenu: {
            styleOverrides: {
                paper: { 
                    pointerEvents: 'auto',
                    borderRadius: 12,
                    boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
                    marginTop: 8,
                    minWidth: 180,
                    padding: '8px 0'
                },
                list: { 
                    pointerEvents: 'auto',
                    padding: '8px 0'
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    padding: '10px 16px',
                    borderRadius: 0,
                    fontSize: '0.875rem',
                    transition: 'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    },
                    '&.Mui-selected': {
                        backgroundColor: 'rgba(37, 99, 235, 0.08)',
                        '&:hover': {
                            backgroundColor: 'rgba(37, 99, 235, 0.12)'
                        }
                    }
                }
            }
        },
        MuiPopover: {
            styleOverrides: {
                paper: {
                    boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
                    borderRadius: 12
                }
            }
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    borderRadius: 6,
                    fontSize: '0.75rem',
                    padding: '6px 12px',
                    backgroundColor: 'rgba(0, 0, 0, 0.85)'
                },
                arrow: {
                    color: 'rgba(0, 0, 0, 0.85)'
                }
            }
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    fontWeight: 600
                }
            }
        },
        MuiBadge: {
            styleOverrides: {
                badge: {
                    fontWeight: 600,
                    minWidth: 20,
                    height: 20,
                    padding: '0 6px'
                }
            }
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    padding: 8
                },
                track: {
                    borderRadius: 22 / 2,
                    opacity: 0.3
                },
                thumb: {
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }
            }
        },
        MuiLinearProgress: {
            styleOverrides: {
                root: {
                    borderRadius: 4,
                    overflow: 'hidden'
                }
            }
        },
        MuiCircularProgress: {
            styleOverrides: {
                circle: {
                    strokeLinecap: 'round'
                }
            }
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    alignItems: 'center'
                },
                standardSuccess: ({ theme }) => ({
                    backgroundColor: alpha(theme.palette.success.main, 0.12),
                    color: theme.palette.success.dark
                }),
                standardInfo: ({ theme }) => ({
                    backgroundColor: alpha(theme.palette.info.main, 0.12),
                    color: theme.palette.info.dark
                }),
                standardWarning: ({ theme }) => ({
                    backgroundColor: alpha(theme.palette.warning.main, 0.12),
                    color: theme.palette.warning.dark
                }),
                standardError: ({ theme }) => ({
                    backgroundColor: alpha(theme.palette.error.main, 0.12),
                    color: theme.palette.error.dark
                })
            }
        }
    },
});